import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Chip,
  Avatar,
  alpha,
  Fade,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Category as CategoryIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useSocket } from "../../context/SocketContext";
import {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  clearError,
  clearSuccess,
  toggleCategory,
} from "../../redux/slices/categorySlice";
import {
  selectCategories,
  selectCategoryLoading,
  selectCategoryError,
  selectCategorySuccess,
  selectCategoryMessage,
} from "../../redux/slices/categorySlice";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Category = () => {
  const dispatch = useDispatch();
  const { socket } = useSocket();

  // Redux state
  const categories = useSelector(selectCategories);
  const loading = useSelector(selectCategoryLoading);
  const error = useSelector(selectCategoryError);
  const success = useSelector(selectCategorySuccess);
  const message = useSelector(selectCategoryMessage);

  // Local state
  const [categoryName, setCategoryName] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [currentCategoryId, setCurrentCategoryId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [categoryImage, setCategoryImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Toast configuration
  const toastConfig = {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  };

  // ============================================
  // ✅ SOCKET EVENT LISTENERS
  // ============================================
  useEffect(() => {
    if (!socket) return;

    // console.log("📡 Category socket listeners setup");

    socket.on('category-created', (data) => {
      // console.log('📂 New category created in real-time:', data);
      toast.success(`📂 ${data?.category?.name} created!`, toastConfig);
    });

    socket.on('category-updated', (data) => {
      toast.info(`📝 ${data?.category?.name} updated!`, toastConfig);
    });

    socket.on('category-deleted', (data) => {
      // console.log('❌ Category deleted in real-time:', data);
      toast.warning(`🗑️ ${data?.categoryName} deleted!`, toastConfig);
    });

    socket.on('category-status-changed', (data) => {
      // console.log('🔄 Category status changed in real-time:', data);
      const status = data?.category?.isActive ? 'activated' : 'deactivated';
      const emoji = data?.category?.isActive ? '✅' : '⛔';
      toast.info(`${emoji} ${data?.category?.name} ${status}!`, toastConfig);
    });

    socket.on('new-notification', (data) => {
      // console.log('🔔 New notification:', data);
      toast.info(data?.notification?.message || 'New notification', toastConfig);
    });

    return () => {
      socket.off('category-created');
      socket.off('category-updated');
      socket.off('category-deleted');
      socket.off('category-status-changed');
      socket.off('new-notification');
    };
  }, [socket]);

  // ============================================
  // ✅ NOTIFICATION SOUND
  // ============================================
  const playNotificationSound = () => {
    try {
      const audio = new Audio('/sounds/notification.mp3');
      audio.volume = 0.3;
      audio.play().catch(e => console.log('Audio play failed'));
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  // Fetch categories on component mount
  useEffect(() => {
    dispatch(getCategories());
  }, [dispatch]);

  // Handle success/error messages - ONLY FOR CREATE/UPDATE/DELETE OPERATIONS
  useEffect(() => {
    if (success) {
      // Only show toast if it's not from socket events
      // Socket events already show their own toasts
      const isSocketEvent = message?.includes('activated') || 
                           message?.includes('deactivated') ||
                           message?.includes('created') ||
                           message?.includes('updated') ||
                           message?.includes('deleted');
      
      if (!isSocketEvent) {
        toast.success(message || "Operation completed successfully!", toastConfig);
        playNotificationSound();
      }
      dispatch(clearSuccess());
    }

    if (error) {
      toast.error(error?.message || "Something went wrong!", toastConfig);
      dispatch(clearError());
    }
  }, [success, error, message, dispatch]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setCategoryImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!categoryName.trim()) {
      toast.warning("Please enter a category name!", toastConfig);
      return;
    }

    if (editMode && currentCategoryId) {
      dispatch(
        updateCategory({
          id: currentCategoryId,
          data: { name: categoryName, image: categoryImage },
        }),
      );
    } else {
      dispatch(createCategory({ name: categoryName, image: categoryImage }));
    }
    resetForm();
  };

  const handleEdit = (category) => {
    setCategoryName(category.name);
    setCurrentCategoryId(category._id);
    setCategoryImage(category.image);
    setEditMode(true);
    setOpenDialog(true);
  };

  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    if (categoryToDelete) {
      dispatch(deleteCategory(categoryToDelete._id));
      setOpenDeleteDialog(false);
      setCategoryToDelete(null);
    }
  };

  const resetForm = () => {
    setCategoryName("");
    setCategoryImage(null);
    setImagePreview(null);
    setCurrentCategoryId(null);
    setEditMode(false);
    setOpenDialog(false);
  };

  // Filter categories
  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get first letter for avatar
  const getInitial = (name) => {
    return name.charAt(0).toUpperCase();
  };

  // Get color for avatar
  const getAvatarColor = (name) => {
    const colors = [
      "#f44336", "#e91e63", "#9c27b0", "#673ab7", "#3f51b5",
      "#2196f3", "#03a9f4", "#00bcd4", "#009688", "#4caf50",
      "#8bc34a", "#cddc39", "#ffeb3b", "#ffc107", "#ff9800",
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const paginatedCategories = filteredCategories.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Box sx={{ mb: 4 }}>
      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        limit={3}
        style={{ marginTop: '10px' }}
      />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <div>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              Categories
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body1" color="text.secondary">
                Manage and organize your product categories
              </Typography>
            </Box>
          </div>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          // sx={{ height: "fit-content", borderRadius: 2, textTransform: "none", px: 3, py: 1 }}
           sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "var(--primary)",
                "&:hover": { bgcolor: "var(--primary-dark)" },
              }}
        >
          New Category
        </Button>
      </Box>

      {/* Search and Action Bar */}
      <Paper
        elevation={0}
        sx={{ p: 3, mb: 3, borderRadius: 3, border: "1px solid", borderColor: "divider" }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search categories by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: "action.active" }} />,
                sx: { borderRadius: 2 },
              }}
              size="small"
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 6 }} sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton
                onClick={() => dispatch(getCategories())}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Chip label={`${filteredCategories.length} categories`} color="primary" variant="outlined" sx={{ borderRadius: 2 }} />
          </Grid>
        </Grid>
      </Paper>

      {/* Main Table */}
      <Paper
        elevation={0}
        sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "hidden" }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
            <CircularProgress />
          </Box>
        ) : filteredCategories.length === 0 ? (
          <Box sx={{ p: 8, textAlign: "center" }}>
            <CategoryIcon sx={{ fontSize: 80, color: "text.disabled", mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No categories found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {searchTerm ? "Try adjusting your search" : "Get started by creating your first category"}
            </Typography>
            <Button variant="contained"  
             sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "var(--primary)",
                "&:hover": { bgcolor: "var(--primary-dark)" },
              }}
              startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
              Create Category
            </Button>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "action.hover" }}>
                    <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Category Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created Date</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCategories?.map((category, index) => (
                    <Fade in={true} key={category._id}>
                      <TableRow hover sx={{ "&:hover": { backgroundColor: "action.hover" }, cursor: "pointer" }}>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Avatar
                              src={category.image || ""}
                              sx={{
                                bgcolor: category.image ? "transparent" : getAvatarColor(category.name),
                                width: 40,
                                height: 40,
                                fontWeight: 600,
                              }}
                            >
                              {!category.image && getInitial(category.name)}
                            </Avatar>
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {category.name}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={category.isActive ? "Active" : "Inactive"}
                            color={category.isActive ? "success" : "error"}
                            onClick={async () => {
                              try {
                                await dispatch(toggleCategory(category?._id)).unwrap();
                                dispatch(getCategories());
                                // ✅ Only show toast here if socket event doesn't handle it
                                // Socket event will show the status change toast
                              } catch (error) {
                                toast.error("Failed to toggle category status!", toastConfig);
                                console.error("Failed to toggle category:", error);
                              }
                            }}
                            sx={{ cursor: "pointer", fontWeight: 500, minWidth: 120 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDate(category.createdAt)}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(category)}
                                sx={{ color: "primary.main", "&:hover": { backgroundColor: alpha("#1976d2", 0.1) } }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(category)}
                                sx={{ color: "error.main", "&:hover": { backgroundColor: alpha("#d32f2f", 0.1) } }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </Fade>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredCategories.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ borderTop: "1px solid", borderColor: "divider" }}
            />
          </>
        )}
      </Paper>

      {/* Add/Edit Category Dialog */}
      <Dialog
        open={openDialog}
        onClose={resetForm}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar sx={{ bgcolor: "var(--primary)" }}>
              {editMode ? <EditIcon /> : <AddIcon />}
            </Avatar>
            <div>
              <Typography variant="h6" >
                {editMode ? "Edit Category" : "Create New Category"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {editMode ? "Update category details" : "Add a new category to organize products"}
              </Typography>
            </div>
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              label="Category Name"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              required
              disabled={loading}
              sx={{ mt: 1 }}
              placeholder="Enter category name (e.g., Appetizers, Main Course)"
              InputProps={{
                startAdornment: <CategoryIcon sx={{ mr: 1, color: "action.active" }} />,
              }}
            />
            <Box sx={{ mt: 3 }}>
              <Button variant="outlined" component="label" fullWidth sx={{ borderRadius: 2,  borderColor: "var(--border)",
                color: "var(--text)", }}>
                {editMode ? "Change Category Image" : "Upload Category Image"}
                <input type="file" hidden accept="image/*" onChange={handleImageChange} />
              </Button>
              {(imagePreview || categoryImage) && (
                <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                  <Avatar
                    src={imagePreview || categoryImage}
                    variant="rounded"
                    sx={{ width: 120, height: 120, borderRadius: 2, border: "1px solid", borderColor: "divider",  }}
                  />
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
            <Button onClick={resetForm} disabled={loading} variant="outlined"
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                borderColor: "var(--border)",
                color: "var(--text)",
              }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!categoryName.trim() || loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
              // sx={{ borderRadius: 2 }}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "var(--primary)",
                "&:hover": { bgcolor: "var(--primary-dark)" },
              }}
            >
              {loading ? "Saving..." : editMode ? "Update Category" : "Create Category"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar sx={{ bgcolor: "error.main" }}>
              <DeleteIcon />
            </Avatar>
            <div>
              <Typography variant="h6">Delete Category</Typography>
              <Typography variant="body2" color="text.secondary">
                Confirm deletion
              </Typography>
            </div>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            This action cannot be undone. All products under this category will need to be reassigned.
          </Alert>
          <Box sx={{ p: 2, backgroundColor: "action.hover", borderRadius: 2, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Category to delete:
            </Typography>
            <Typography variant="h6" sx={{ mt: 1 }}>
              {categoryToDelete?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              ID: {categoryToDelete?._id}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setOpenDeleteDialog(false)} disabled={loading} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
            sx={{ borderRadius: 2 }}
          >
            {loading ? "Deleting..." : "Delete Category"}
          </Button>
        </DialogActions>
      </Dialog>

      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.5; transform: scale(1.2); }
          }
        `}
      </style>
    </Box>
  );
};

export default Category;